import { call, all, put, takeEvery } from 'redux-saga/effects';

import URLs from '../../../constants/urls';
import { apiPython as api } from '../../../service/api';
import {
  deleteQuestionAction,
  getQuestionsPreviewModificationsAction,
  getReviewModificationsAction,
  getUserModificationsAction,
  sendSMEEmailAction,
  setQuestionDetails,
  setReviewModificationsData,
  setReviewModificationsLoader,
  setUserModificationsData
} from '../../features/approveReviewModifications/approveReviewModificationsSlice';
import { getSessionStorage } from '../../../utils/helpers';
import { normalizeSmeStatusList, unwrapApiPayload } from '../../../utils/apiPayload';
import moment from 'moment';

export function* getApproveReviewModificationsSaga(action) {
  yield put(setReviewModificationsLoader(true));
  const userId = getSessionStorage('USER_ID');

  try {
    const permissionLevel = String(getSessionStorage('PERMISSION_LEVEL') || 'A').toUpperCase();
    const response = yield api.get(
      `${URLs.GET_APPROVE_REVIEW_MODIFICATIONS}?loggedUserID=${encodeURIComponent(userId || '')}&permissionLevel=${encodeURIComponent(permissionLevel)}`
    );

    const data = normalizeSmeStatusList(response.data);

    yield put(setReviewModificationsData(data));
    yield put(setReviewModificationsLoader(false));
  } catch (error) {
    yield put(setReviewModificationsLoader(false));
  }
}

const buildSkillMap = (skills, map = {}) => {
  skills.forEach((s) => {
    const id = s.Id ?? s.id;
    const name = s.SkillName ?? s.skillName ?? '';
    if (id != null) map[id] = name;
    const children = s.SecondarySkill ?? s.secondarySkill ?? [];
    if (children.length) buildSkillMap(children, map);
  });
  return map;
};

const complexityLabel = (c) => c === 1 ? 'Easy' : c === 2 ? 'Medium' : c === 3 ? 'Hard' : '';

export function* getUserModificationsSaga(action) {
  yield put(setReviewModificationsLoader(true));

  try {
    const response = yield api.get(`${URLs.GET_USER_MODIFICATIONS}?SamID=${action.payload}`);

    const responseData = unwrapApiPayload(response.data) ?? {};
    const skillArea = responseData.SkillArea ?? responseData.skillArea ?? [];
    const skillMap = buildSkillMap(skillArea);

    const questionsList = responseData.QuestionsList ?? responseData.questionsList ?? [];
    const data = questionsList.map((item) => {
      const skillId = item.Skill_Id ?? item.skill_Id ?? item.skillId;
      const complexity = item.Complexity ?? item.complexity;
      return {
        ...item,
        id: item.Id ?? item.id,
        prompt: item.Prompt ?? item.prompt,
        skills: skillMap[skillId] ?? '',
        complexity: complexityLabel(complexity),
        lastEditedBy: item.LastEditedBy ?? item.lastEditedBy ?? '',
        lastEditedOn: moment(item.LastEditedOn ?? item.lastEditedOn).format('DD-MMM-YYYY'),
        questionType: item.QuestionType ?? item.questionType,
      };
    });

    yield put(setUserModificationsData(data));
    yield put(setReviewModificationsLoader(false));
  } catch (error) {
    yield put(setReviewModificationsLoader(false));
  }
}

export function* getQuestionDetailsForPreviewModificationsSaga(action) {
  yield put(setReviewModificationsLoader(true));

  try {
    const response = yield api.get(`${URLs.GET_QUESTION_DETAILS_FOR_PREVIEW}?questionId=${action.payload}`);

    const data = unwrapApiPayload(response.data) ?? {};
    yield put(setQuestionDetails(data));
    yield put(setReviewModificationsLoader(false));
  } catch (error) {
    yield put(setReviewModificationsLoader(false));
  }
}

export function* deleteQuestionSaga(action) {
  yield put(setReviewModificationsLoader(true));

  try {
    const response = yield api.post(URLs.DELETE_QUESTION, action.payload);

    yield call(getUserModificationsSaga, { payload: action.payload.userSamId });

    yield put(setReviewModificationsLoader(false));
  } catch (error) {
    yield put(setReviewModificationsLoader(false));
  }
}

export function* sendSMEEmailSaga(action) {
  yield put(setReviewModificationsLoader(true));

  try {
    yield api.post(`${URLs.SEND_SME_EMAIL}`, action.payload);

    yield put(setReviewModificationsLoader(false));
  } catch (error) {
    yield put(setReviewModificationsLoader(false));
  }
}

export function* watchApproveReviewModifications() {
  yield takeEvery(getReviewModificationsAction.type, getApproveReviewModificationsSaga);
  yield takeEvery(getUserModificationsAction.type, getUserModificationsSaga);
  yield takeEvery(getQuestionsPreviewModificationsAction.type, getQuestionDetailsForPreviewModificationsSaga);
  yield takeEvery(sendSMEEmailAction.type, sendSMEEmailSaga);
  yield takeEvery(deleteQuestionAction.type, deleteQuestionSaga);
}

export default function* approveReviewModificationsSaga() {
  yield all([watchApproveReviewModifications()]);
}
